import type { CSSProperties } from "react";
import {
  BEVEL_CHIP_ON,
  BEVEL_INSET_DOWN,
  BEVEL_INSET_DOWN_NAVY,
  BEVEL_INSET_NAVY,
  BEVEL_INSET_WHITE,
  BEVEL_NAVY_FACE,
  BEVEL_NAVY_FACE_DOWN,
  BEVEL_WHITE_FACE,
  BEVEL_WHITE_FACE_DOWN,
  PEWTER_GAP,
  PEWTER_OUTLINE,
  pewterEdge,
  solid,
  type PewterStyle,
} from "@majourneys/shared/ui";

/**
 * The pewter frame, cut for the booking surfaces: a white band with a lit
 * hairline running across it, then the surface inside. The geometry comes from
 * `@majourneys/shared/ui`; this file only fixes the colours and the handful of
 * shapes the funnel needs, so every booking panel is framed the same way.
 *
 * Styles are objects rather than Tailwind classes because the package is also
 * served by the city sites, which do not load the web app's stylesheet. The
 * few stateful pieces (a field's focus, a chip's pressed state) need CSS, and
 * that CSS is injected by `BookingSkinScope` only when the skin is on.
 */
export const PW = {
  navy: "#110E39",
  gold: "#E5B265",
  goldDark: "#8A6425",
  muted: "#5a5d77",
  hairline: "rgba(17,14,57,0.09)",
  /** The card's own 1px edge, inside the band. */
  innerHairline: "rgba(17,14,57,0.13)",
  /** The soft-bevel navy face (owner, 2026-10-25). */
  navyGradient: BEVEL_NAVY_FACE,
  /** The ink tint a sheet sits its small caps and sunken rows on. */
  softTint: "rgba(17,14,57,0.055)",
  green: "#1A7A44",
  greenTint: "rgba(31,168,85,0.15)",
  red: "#9B2C2C",
  redTint: "rgba(190,60,60,0.13)",
  goldTint: "rgba(229,178,101,0.2)",
  cardFill:
    "linear-gradient(135deg, rgba(252,252,254,1) 0%, rgba(248,248,251,1) 45%, rgba(251,251,253,1) 100%)",
  /** A sheet's body tone, top to bottom: cooler and faintly lavender. */
  sheetFill:
    "linear-gradient(180deg, rgba(252,252,254,1) 0%, rgba(246,246,251,1) 62%, rgba(242,242,248,1) 100%)",
  primaryShadow: `0 10px 24px -12px rgba(17,14,57,0.8), ${BEVEL_INSET_NAVY}`,
  panelShadow: "0 8px 30px rgba(17,14,57,0.06)",
  /** A control at rest: white with one navy hairline, never the band. */
  controlEdge: "rgba(17,14,57,0.16)",
  controlFill: "rgba(255,255,255,0.92)",
} as const;

export interface PewterPanelOptions {
  /** The surface inside the band. A gradient, or a colour wrapped in `solid()`. */
  fill?: string;
  /** Width of the band. Panels take the house 4px, fields and chips 3px. */
  gap?: number;
  /** The card's own edge. Navy to mark a selection, red to mark an error. */
  hairline?: string;
  /** The drop shadow under the panel; `null` for none. */
  shadow?: string | null;
}

/**
 * The frame on a single element: the band is the element's own transparent
 * border, the fill paints inside it, and the card's hairline is an inset
 * shadow, which is clipped to the padding box, exactly where the edge is.
 */
export function pewterPanel(radius: number, options: PewterPanelOptions = {}): PewterStyle {
  const shadow = options.shadow === undefined ? PW.panelShadow : options.shadow;
  return {
    ...pewterEdge(options.fill ?? PW.cardFill, radius, {
      tone: "light",
      style: "pewter",
      width: options.gap ?? PEWTER_GAP,
      base: solid("rgba(255,255,255,1)"),
    }),
    boxShadow: [
      `0 0 0 1px ${PEWTER_OUTLINE}`,
      ...(shadow ? [shadow] : []),
      `inset 0 0 0 1px ${options.hairline ?? PW.innerHairline}`,
    ].join(", "),
  };
}

/**
 * The approved pewter panel (owner, 2026-09-24, "Softer" from the lab): a 4px
 * pale band, a very faint navy outer edge, a soft drop shadow and a light navy
 * inner hairline, every edge about half as dark as the first version. Shared
 * by the package cards and the transport page cards. `selected` swaps the inner hairline for a navy 1.5px edge.
 */
export function pewterPanelApproved(radius: number, selected = false): CSSProperties {
  return {
    border: "4px solid transparent",
    borderRadius: radius,
    background:
      "linear-gradient(135deg, #FCFCFE, #F8F8FB 45%, #FBFBFD) padding-box, " +
      "linear-gradient(155deg, #FFFFFF, #EDEFF5 28%, #F0F2F7 55%, #F5F6FA 78%, #FFFFFF) border-box",
    boxShadow: [
      "0 0 0 1px rgba(17,14,57,0.04)",
      selected ? "0 10px 30px rgba(17,14,57,0.12)" : "0 8px 30px rgba(17,14,57,0.05)",
      selected ? "inset 0 0 0 1.5px #110E39" : "inset 0 0 0 1px rgba(17,14,57,0.07)",
    ].join(", "),
  };
}

/**
 * A form field's box: white with one hairline, no band (owner, 2026-09-05:
 * no gradients on fields). The hairline is a box-shadow rather than a border
 * so a state change never moves the text inside.
 */
export function pewterField(radius = 16, options: Pick<PewterPanelOptions, "hairline"> = {}): PewterStyle {
  return {
    borderRadius: radius,
    background: PW.controlFill,
    // NOT inset. An inset ring paints on the padding box, so any child with a
    // background of its own covers it — which is exactly what happened to the
    // phone field: the country button and the number input painted over the
    // hairline and the whole control looked borderless next to the plain text
    // inputs beside it. An outset ring sits outside the border box and no
    // child can reach it.
    boxShadow: `0 0 0 1px ${options.hairline ?? PW.controlEdge}`,
  };
}

/** A sunken row inside a panel: the ink tint, no frame of its own. */
export const pewterInset: CSSProperties = { background: PW.softTint };

/** The band a panel's footer sits on, with a hairline where it meets the body. */
export const pewterFooter: CSSProperties = {
  background: PW.softTint,
  boxShadow: `inset 0 1px 0 ${PW.hairline}`,
};

/** A white button at rest: the soft-bevel face over one navy hairline. Never a field. */
export const pewterButton: CSSProperties = {
  background: BEVEL_WHITE_FACE,
  boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px ${PW.controlEdge}`,
};

/** The primary button: the navy gradient with its lift. */
export const pewterPrimary: CSSProperties = {
  background: PW.navyGradient,
  color: "#fff",
  boxShadow: PW.primaryShadow,
};

function cssText(style: PewterStyle): string {
  return Object.entries(style)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}: ${typeof v === "number" ? `${v}px` : v}`)
    .join("; ");
}

/* Outset, for the same reason as `pewterField`'s resting ring: a composite
   field's children paint over an inset one, so focus and error would be
   invisible on exactly the control most likely to show an error. */
const focusShadow = `0 0 0 1.5px ${PW.navy}`;
const errorShadow = `0 0 0 1px rgba(155,44,44,0.75)`;
const validShadow = `0 0 0 1px rgba(26,122,68,0.55)`;

/**
 * The stateful pieces, as a stylesheet `BookingSkinScope` injects once.
 *
 * `.mj-pw-field` is a text field or the box around one: white with one
 * hairline at rest, a navy edge on focus or while its picker is open, a red
 * one when invalid, a green one when marked valid. Fields do not wear the
 * band (owner: no gradients on fields).
 * `.mj-pw-chip` is a pill you press: the soft-bevel white face with one navy
 * hairline, the navy bevel face when chosen, sinking while held. Controls never wear the band (owner, 2026-09-05: no gradient
 * border on buttons); the frame is for surfaces you read.
 */
export const PEWTER_CSS = `
.mj-pw-field { ${cssText(pewterField(16))}; }
.mj-pw-field:focus, .mj-pw-field:focus-within, .mj-pw-field[aria-expanded="true"], .mj-pw-field.mj-pw-open { box-shadow: ${focusShadow}; }
.mj-pw-field[aria-invalid="true"], .mj-pw-field.mj-pw-invalid { box-shadow: ${errorShadow}; }
.mj-pw-field.mj-pw-valid { box-shadow: ${validShadow}; }
.mj-pw-chip { border-radius: 999px; border: 1.5px solid ${PW.controlEdge}; background: ${BEVEL_WHITE_FACE}; box-shadow: ${BEVEL_INSET_WHITE}; color: ${PW.navy}; }
.mj-pw-chip:hover { border-color: rgba(17,14,57,0.34); }
.mj-pw-chip:active { background: ${BEVEL_WHITE_FACE_DOWN}; box-shadow: ${BEVEL_INSET_DOWN}; }
.mj-pw-chip[aria-pressed="true"] { border-color: transparent; background: ${PW.navyGradient}; box-shadow: ${BEVEL_CHIP_ON}; color: #fff; }
.mj-pw-chip[aria-pressed="true"]:active { background: ${BEVEL_NAVY_FACE_DOWN}; box-shadow: ${BEVEL_INSET_DOWN_NAVY}; }
`;
